import React, {useCallback, useEffect} from "react";
import {useTranslation} from "react-i18next";
import {PublisherBookModel} from "../../../models/publisher/publisher-book.model";
import {useHistory, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {showSnackSuccess, toggleLoader} from "../../../store/global/global-slice";
import {PublisherService} from "../../../services/publisher.service";
import {Grid, Hidden, Tab, Tabs} from "@material-ui/core";
import {StyledButton} from "../../../components/ui-base/button";
import styled from "styled-components";
import {Colors} from "../../../theme/colors";
import {ContentHeader} from "../../../components/ui-base/content-header";
import Chip from "@material-ui/core/Chip";
import {CustomTabPanel} from "../../../components/ui-base/tab-panel";
import {BookDescriptionDialog} from "../../../components/publisher/book-descriptions-dialog";
import {PublisherLeftBookDetails} from "../../../components/publisher/my-books/left-book-details";
import {AllLanguages} from "../../../data/languages";
import {CountryModel} from "../../../models/country.model";
import {RootState} from "../../../store/root-reducer";
import {LangModel} from "../../../models/lang.model";
import {PublisherProposalModel} from "../../../models/publisher/publisher-proposal.model";
import {PublisherApplicationToSend} from "../../../components/publisher/my-books/publisher-application-to-send";
import {SuccessModal} from "../../../components/ui-base/success-modal";
import {ApplicationStatusEnum} from "../../../models/application-status.enum";
import {PublisherSubmittedModel} from "../../../models/publisher/publisher-submitted.model";
import {PublisherApplicationSubmitted} from "../../../components/publisher/my-books/publisher-application-submitted";
import {BaseService} from "../../../services/base.service";

const PublisherMyBookDetailsPage = () => {
	const [openDescriptionDialog, setOpenDescriptionDialog] = React.useState<boolean>(false);
	const [proposals, setProposals] = React.useState<PublisherProposalModel[]>([]);
	const [toAccess, setToAccess] = React.useState<PublisherProposalModel[]>([]);
	const [submitted, setSubmitted] = React.useState<PublisherSubmittedModel[]>([]);
	const [book, setBook] = React.useState<PublisherBookModel>(null);
	const [tabActive, setTabActive] = React.useState(0);
	const [openSuccessModal, setOpenSuccessModal] = React.useState(false);
	const [successModalDesc, setSuccessModalDesc] = React.useState('');

	const { t } = useTranslation('publisher');
	const history = useHistory();
	const dispatch = useDispatch();
	const urlParams = useParams();
	const id: string = urlParams['id'] as string;

	const languages: {language: string, code: string}[] = AllLanguages;

	const countries: CountryModel[] = useSelector(
		(state: RootState) => state.countries.countries
	);

	const loadProposals = useCallback(async () => {
		dispatch(toggleLoader(true));

		const proposals = await PublisherService.getProposals(id, 0, 4, [ApplicationStatusEnum.Created, ApplicationStatusEnum.WithCont, ApplicationStatusEnum.Analyzed]);
		setProposals(proposals);

		const proposalsAskAccess = await PublisherService.getProposals(id, 0, 4, [ApplicationStatusEnum.AskAccess]);
		setToAccess(proposalsAskAccess);

		dispatch(toggleLoader(false));
	}, [id, dispatch]);

	const loadSubmitted = useCallback(async () => {
		const submitted = await PublisherService.getSubmitted(id);

		setSubmitted(submitted);
	}, [id]);

	useEffect(() => {
		const loadBook = async (id: string) => {
			dispatch(toggleLoader(true));

			const bookTmp = await PublisherService.getBook(id);
			setBook(bookTmp);

			await loadProposals();
			await loadSubmitted();

			dispatch(toggleLoader(false));
		}

		if (id) {
			loadBook(id);
		}
	}, [id, dispatch, loadProposals, loadSubmitted]);

	const getLangByCode = (code: string): LangModel => {
		return languages.find(lang => lang.code === code);
	}

	const getCountryByIso = (iso: string): CountryModel => {
		return countries.find(country => country.iso === iso);
	}

	const submitToPublisher = async (id: string, publisherActive: boolean) => {
		dispatch(toggleLoader(true));
		const submittedToPublisher = await PublisherService.submitToPublisher(id);

		if (submittedToPublisher) {
			setSuccessModalDesc((publisherActive) ? t('applicationToSend.sent1') : t('applicationToSend.sent2'));
			setOpenSuccessModal(true);
			await loadProposals();
			await loadSubmitted();
		} else {
			dispatch(toggleLoader(false));
		}
	}

	const confirmPublisher = async (id: string) => {
		dispatch(toggleLoader(true));
		const confirmedPublisher = await PublisherService.confirmPublisher(id);

		if (confirmedPublisher) {
			dispatch(showSnackSuccess(t('detailsMyBook.bookSendToPublisher')));
			await loadProposals();
			await loadSubmitted();
		}

		dispatch(toggleLoader(false));
	}

	const declinePublisher = async (id: string) => {
		dispatch(toggleLoader(true));
		const confirmedPublisher = await PublisherService.declinePublisher(id);

		if (confirmedPublisher) {
			dispatch(showSnackSuccess(t('detailsMyBook.bookNotSendToPublisher')));
			await loadProposals();
			await loadSubmitted();
		}

		dispatch(toggleLoader(false));
	}

	return (
		<Grid style={{backgroundColor: '#fff', padding: '1rem'}} container direction={"row"}>
			{book &&
				<>
                    <BookDescriptionDialog
                        open={openDescriptionDialog}
                        onClose={(value) => {
							setOpenDescriptionDialog(false);
						}}
                        description={book.description}
                        summary={book.summary}
                        fragment={book.fragment}
						review={book.review}
						tableOfContest={book.table_of_contents}
                    />

                    <Hidden mdDown>
                        <PublisherLeftBookDetails book={book} />
                    </Hidden>

                    <Grid item container direction={"column"} xs spacing={2}>
                        <Grid item container direction={"column"} style={{position: 'relative'}}>
                            <Grid item>
                                <StyledH4>{book.original_title}</StyledH4>
                            </Grid>
                            <Grid item>
                                <StyledH6>{book.en_title}</StyledH6>
                            </Grid>
                            <Grid item>
                                <StyledDate>{t('common:created')}: {new Date(book.created).toLocaleString()}</StyledDate>
                            </Grid>

                            <Grid item>
                                <StyledDate>{t('common:updated')}: {new Date(book.updated).toLocaleString()}</StyledDate>
                            </Grid>

                            <StyledEditContainer>
                                <StyledButton type="submit" color={'violet'} onClick={() => {
									history.push('/publisher/my-books/edit/' + book.id);
								}} style={{marginRight: '0.5rem'}}>{t('common:edit')}</StyledButton>

                                {/*<StyledButton type="submit" color={'red'} onClick={() => {*/}
								{/*	setOpenConfirmDialog(true);*/}
								{/*}}>{t('common:delete')}</StyledButton>*/}
                            </StyledEditContainer>
                        </Grid>

                        <Grid item>
                            <ContentHeader title={t('detailsMyBook.detailsInfo')}/>
                        </Grid>

                        <Grid item container direction={"row"}>
                            <Grid item xs={12} md={4}>
                                <Grid item>
                                    <StyledDetailsSpan>{t('publisher:addBookNew.form.isbn')}:</StyledDetailsSpan>
                                </Grid>

                                <Grid item>
                                    <StyledDetailsP>{book.isbn || '---'}</StyledDetailsP>
                                </Grid>
							</Grid>
                            <Grid item xs={12} md={4}>
                                <Grid item>
                                    <StyledDetailsSpan>{t('publisher:addBookNew.form.author')}:</StyledDetailsSpan>
                                </Grid>

                                <Grid item>
                                    <StyledDetailsP>{book.authors || '---'}</StyledDetailsP>
                                </Grid>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Grid item>
                                    <StyledDetailsSpan>{t('publisher:addBookNew.form.originalLang')}:</StyledDetailsSpan>
                                </Grid>

                                <Grid item>
                                    <StyledDetailsP>{book.book_language || '---'}</StyledDetailsP>
                                </Grid>
                            </Grid>
						</Grid>

                        <Grid item container direction={"row"}>
                            <Grid item xs={12} md={6}>
                                <StyledSpan1>{t('publisher:addBookNew.form.translatedTo')}:</StyledSpan1>
                                <Grid container direction={'row'} spacing={1}>
									{book.translations?.map((translation, index) => (
										<Grid item key={index} style={{maxWidth: '100%'}}>
											<StyledTagChip size="small" label={getLangByCode(translation.language).language} style={{maxWidth: '100%'}} />
										</Grid>
									))}
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <StyledSpan1>{t('publisher:addBookNew.form.countryWithLicense')}:</StyledSpan1>
                                <Grid container direction={'row'} spacing={1}>
									{book.countries_sold_to?.map((country, index) => (
										<Grid item key={index} style={{maxWidth: '100%'}}>
											<StyledTagChip size="small" label={getCountryByIso(country).name} style={{maxWidth: '100%'}}/>
										</Grid>
									))}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item container direction={"row"}>
                            <Grid item xs={12} md={6}>
                                <StyledSpan1>{t('common:categories')}:</StyledSpan1>
                                <Grid container direction={'row'} spacing={1}>
									{book.categories.map((category) => (
										<Grid item key={category.id} style={{maxWidth: '100%'}}>
											<StyledCategoryChip size="small" label={category.name} style={{maxWidth: '100%'}} />
										</Grid>
									))}
                                </Grid>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <StyledSpan1>{t('common:tags')}:</StyledSpan1>
                                <Grid container direction={'row'} spacing={1}>
									{book.tags.filter((tag) => tag.tag_type === 'tag').map((tag) => (
										<Grid item key={tag.id} style={{maxWidth: '100%'}}>
											<StyledTagChip size="small" label={tag.name} style={{maxWidth: '100%'}}/>
										</Grid>
									))}
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item>
                            <Tabs value={tabActive} onChange={(event, newValue) => { setTabActive(newValue) }}>
                                <Tab label={t('common:bookDescriptionDialog.tabTitle1')} style={{fontSize: '0.9rem'}}/>
                                <Tab label={t('common:bookDescriptionDialog.tabTitle2')} style={{fontSize: '0.9rem'}}/>
                                <Tab label={t('common:bookDescriptionDialog.tabTitle3')} style={{fontSize: '0.9rem'}}/>
                                <Tab label={t('common:bookDescriptionDialog.tabTitle4')} style={{fontSize: '0.9rem'}}/>
                                <Tab label={t('common:bookDescriptionDialog.tabTitle5')} style={{fontSize: '0.9rem'}}/>
                            </Tabs>

                            <CustomTabPanel value={tabActive} index={0}>
								{book.fragment &&
                                    <StyledDescription>{BaseService.cutText(book.fragment, 1500)}</StyledDescription>
								}

								{!book.fragment &&
                                    <StyledEmptyDescription>{t('common:bookDescriptionDialog.fillPart')}</StyledEmptyDescription>
								}

								{book.fragment.length > 1500 &&
                                    <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
										setOpenDescriptionDialog(true);
									}}>{t('common:bookDescriptionDialog.showPart')}</StyledButton>
								}
                            </CustomTabPanel>

                            <CustomTabPanel value={tabActive} index={1}>
								{book.description &&
                                    <StyledDescription>{BaseService.cutText(book.description, 1500)}</StyledDescription>
								}

								{!book.description &&
                                    <StyledEmptyDescription>{t('common:bookDescriptionDialog.fillDesc')}</StyledEmptyDescription>
								}

								{book.description.length > 1500 &&
                                    <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
										setOpenDescriptionDialog(true);
									}}>{t('common:bookDescriptionDialog.showDesc')}</StyledButton>
								}
                            </CustomTabPanel>

                            <CustomTabPanel value={tabActive} index={2}>
								{book.summary &&
                                    <StyledDescription>{BaseService.cutText(book.summary, 1500)}</StyledDescription>
								}

								{!book.summary &&
                                    <StyledEmptyDescription>{t('common:bookDescriptionDialog.fillSummary')}</StyledEmptyDescription>
								}

								{book.summary.length > 1500 &&
                                    <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
										setOpenDescriptionDialog(true);
									}}>{t('common:bookDescriptionDialog.showSummary')}</StyledButton>
								}
                            </CustomTabPanel>

                            <CustomTabPanel value={tabActive} index={3}>
								{book.review &&
                                    <StyledDescription>{BaseService.cutText(book.review, 1500)}</StyledDescription>
								}

								{!book.review &&
                                    <StyledEmptyDescription>{t('common:bookDescriptionDialog.fillReview')}</StyledEmptyDescription>
								}

								{book.review.length > 1500 &&
                                    <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
										setOpenDescriptionDialog(true);
									}}>{t('common:bookDescriptionDialog.showReview')}</StyledButton>
								}
                            </CustomTabPanel>

                            <CustomTabPanel value={tabActive} index={4}>
								{book.table_of_contents &&
                                    <StyledDescription>{BaseService.cutText(book.table_of_contents, 1500)}</StyledDescription>
								}

								{!book.table_of_contents &&
                                    <StyledEmptyDescription>{t('common:bookDescriptionDialog.fillTableOfContents')}</StyledEmptyDescription>
								}

								{book.table_of_contents.length > 1500 &&
                                    <StyledButton type={"button"} style={{marginTop: '1.5rem'}} color={"transparentViolet"} onClick={() => {
										setOpenDescriptionDialog(true);
									}}>{t('common:bookDescriptionDialog.showTableOfContents')}</StyledButton>
								}
                            </CustomTabPanel>
                        </Grid>

                        <Grid item>
                            <ContentHeader title={t('detailsMyBook.sentBook')}/>
                        </Grid>

                        <Grid container item direction={'row'} spacing={1} justifyContent={"center"}>
							{proposals.map((proposalData) =>
								<PublisherApplicationToSend
									key={proposalData.id}
									proposal={proposalData}
									submitToPublisher={() => {
										submitToPublisher(proposalData.id, proposalData.publisherto.publisher_type === 'ACTIVE')
									}}
								/>
							)}
                        </Grid>

                        <Grid item container direction={"row"} justifyContent={"center"}>
                            <StyledButton type={"button"} color={"transparentViolet"} onClick={() => {
								history.push('/publisher/my-books/publishers/' + book?.id);
							}}>{t('detailsMyBook.showAllPublishers')}</StyledButton>
                        </Grid>

						<Grid item>
							<ContentHeader title={t('detailsMyBook.bookSubmitted')}/>
						</Grid>

						<Grid container item direction={'row'} spacing={1} justifyContent={"center"}>
							{submitted.map((submittedData) =>
								<PublisherApplicationSubmitted
									key={submittedData.id}
									submittedModel={submittedData}
									updateStatus={() => {loadSubmitted()}}
								/>
							)}
						</Grid>

						{toAccess.length > 0 &&
							<>
								<Grid item>
									<ContentHeader title={t('detailsMyBook.toAccessMarketTitle')}/>
								</Grid>

								<Grid container item direction={'row'} spacing={1} justifyContent={"center"}>
									{toAccess.map((proposalData) =>
										<PublisherApplicationToSend
											key={proposalData.id}
											proposal={proposalData}
											declinePublisher={() => {declinePublisher(proposalData.id)}}
											confirmPublisher={() => {confirmPublisher(proposalData.id)}}
										/>
									)}
								</Grid>
							</>
						}
					</Grid>
				</>
			}
			<SuccessModal open={openSuccessModal} setOpen={setOpenSuccessModal} title={successModalDesc}/>
		</Grid>
	)
}

const StyledH4 = styled('h6')`
  font-size: 1.5rem;
  color: ${Colors.violet};
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const StyledH6 = styled('h6')`
  font-size: 1rem;
  color: ${Colors.violet};
  font-weight: 500;
  margin-bottom: 0.9rem;
`;

const StyledDate = styled('p')`
  color: ${Colors.grayText};
  font-size: 0.8rem;
  font-weight: 400;
`;

const StyledEditContainer = styled('div')`
  position: absolute;
  right: 0;
`;

const StyledSpan1 = styled('span')`
  font-weight: 500;
  font-size: 0.9rem;
  color: ${Colors.darkGray};
`;

const StyledCategoryChip = styled(Chip)`
  background-color: #FBF5FF;
  color: ${Colors.lightViolet};
  border: 1px solid ${Colors.lightViolet};
  font-weight: 500;
`;

const StyledTagChip = styled(Chip)`
  background-color: #F5F5F5;
  color: #424242;
  border: 1px solid #C2C2C2;
  font-weight: 500;
`;

const StyledDescription = styled('p')`
  font-size: 0.9rem;
  white-space: pre-line;
`;

const StyledEmptyDescription = styled('p')`
  font-size: 0.9rem;
  white-space: pre-line;
  color: ${Colors.grayText};
`;

const StyledDetailsSpan = styled('span')`
  color: ${Colors.grayText};
  font-weight: 400;
  font-size: 0.9rem;
`;

const StyledDetailsP = styled('p')`
  font-weight: 400;
  font-size: 0.9rem;
`;

export default PublisherMyBookDetailsPage;
